/**
 * Per-map seen-area heatmap (docs/0006_osd-viewer-port.md, Seen-area heatmap): records what the user
 * looked at on the OSD viewer and paints it on the navigator.
 *
 * `host` is the BaseSlideMapOSD instance: viewer, slide, getSeenAreaViewLevel(), getSeenAreaLevels().
 */
import {DwellTracker} from './dwellTracker';
import {HoldToggle} from './holdToggle';
import {computeSeenScore} from './seenAreaScore';
import {SeenAreaSession, SESSION_STATUS} from './seenAreaSession';
import {forEachSetBit, hasTiledLevels, levelKey, STITCHED, TILED} from './seenAreaFormat';
import {boundingBox, fieldsInBox, getFieldRects, stitchedTilesInBox, visibleFraction} from './seenAreaGeometry';
import {getImageDimensions, getStitchedTileSize} from '../utils/tileSourceUtils';

export const SEEN_TICK_MS = 250;
const HEAT_RGB = '0, 245, 72';
const LEVEL_ALPHA = 0.22;
const VISIBLE_STORAGE_KEY = 'osd-seen-heatmap-visible';

const loadPersistentVisible = () => {
    try {
        return localStorage.getItem(VISIBLE_STORAGE_KEY) === '1';
    } catch (e) {
        return false;
    }
};

export class SeenAreaController {
    constructor(host) {
        this.host = host;
        this.session = null;
        this.dwell = new DwellTracker();
        this.toggle = new HoldToggle({persistent: loadPersistentVisible()});
        this.viewingUser = null;
        this.users = null;
        this._displayBits = null;
        this._listeners = new Set();
        this._paths = null;
        this._pathsVersion = null;
        this._fieldRects = null;
        this._fieldRectsById = null;
        this._fieldRectsTxty = null;
        this._fieldRectsVersion = 0;
        this._canvas = null;
        this._drawRaf = null;
        this._tickTimer = null;
        this._snapshot = this._buildSnapshot();
    }

    get viewer() {
        return this.host.viewer;
    }

    get slide() {
        return this.host.props.slide;
    }

    start = () => {
        this.session = new SeenAreaSession(this.slide, this._onSessionChange);
        this.session.start();
        this._tickTimer = setInterval(this.tick, SEEN_TICK_MS);
        this._attachViewerHandlers();
    };

    dispose = () => {
        clearInterval(this._tickTimer);
        if (this._drawRaf != null) cancelAnimationFrame(this._drawRaf);
        this._detachViewerHandlers();
        this.session?.dispose();
        this._canvas?.parentNode?.removeChild(this._canvas);
        this._canvas = null;
        this._listeners.clear();
    };

    // ------------------------------------------------------------------ state for the controls

    subscribe = (listener) => {
        this._listeners.add(listener);
        listener(this._snapshot);
        return () => this._listeners.delete(listener);
    };

    getSnapshot = () => this._snapshot;

    _buildSnapshot = () => {
        const session = this.session;
        const ready = session?.status === SESSION_STATUS.READY;
        const bits = this._displayBits || session?.seen;
        return {
            status: session?.status || SESSION_STATUS.IDLE,
            visible: this.toggle.visible,
            persistentVisible: this.toggle.persistent,
            hasTissue: ready && !!session.tissue,
            score: ready && session.tissue && bits
                ? computeSeenScore(bits, session.tissue, session.layout, this.host.getSeenAreaLevels())
                : null,
            viewingUser: this.viewingUser,
            users: this.users,
        };
    };

    _emit = () => {
        this._snapshot = this._buildSnapshot();
        this._listeners.forEach(listener => listener(this._snapshot));
    };

    _onSessionChange = () => {
        this._emit();
        this.scheduleDraw();
    };

    /** Zoom bounds and the digital-zoom mode change which levels count, so the score is recomputed. */
    refreshScore = () => this._emit();

    // ------------------------------------------------------------------ recording

    /** Image-space polygon of the screen, rotation included (four corners of the rotated bounds). */
    _viewportPolygon = () => {
        const viewer = this.viewer;
        const item = viewer?.world?.getItemCount() > 0 ? viewer.world.getItemAt(0) : null;
        if (!item || !viewer.viewport) return null;
        const bounds = viewer.viewport.getBounds(true);
        const corners = [bounds.getTopLeft(), bounds.getTopRight(), bounds.getBottomRight(), bounds.getBottomLeft()];
        const points = corners.map(c => {
            const p = item.viewportToImageCoordinates(c.x, c.y, true);
            return [p.x, p.y];
        });
        return points.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y)) ? points : null;
    };

    /** `txty` usually reaches the slide after the viewer opens, so the field list follows it. */
    _getFieldRects = () => {
        const slide = this.slide;
        if (this._fieldRects === null || this._fieldRectsTxty !== slide?.txty) {
            this._fieldRectsTxty = slide?.txty;
            this._fieldRects = getFieldRects(slide?.txty, {width: slide?.tile_width, height: slide?.tile_height}, this.session.grid.x_fields);
            this._fieldRectsById = new Map(this._fieldRects.map(field => [field.idx, field.rect]));
            this._fieldRectsVersion += 1;
        }
        return this._fieldRects;
    };

    /** H5: candidates at the level shown for the current magnification only, never prefetched levels. */
    _candidates = (view, polygon) => {
        const bbox = boundingBox(polygon);
        const slide = this.slide;
        if (view.kind === TILED) return fieldsInBox(this._getFieldRects(), bbox);
        if (view.level < this.session.grid.min_level || view.level > this.session.grid.max_level) return [];
        return stitchedTilesInBox(this.session.grid, view.level, getStitchedTileSize(slide), getImageDimensions(slide), bbox);
    };

    tick = () => {
        const session = this.session;
        if (session?.status !== SESSION_STATUS.READY || document.hidden || this.host.isPinchGestureActive()) {
            if (document.hidden) this.dwell.reset();
            return;
        }
        const view = this.host.getSeenAreaViewLevel();
        const polygon = view && this._viewportPolygon();
        if (!polygon) {
            this.dwell.reset();
            return;
        }
        const fractions = new Map();
        for (const {idx, rect} of this._candidates(view, polygon)) {
            fractions.set(`${view.kind}|${view.level}|${idx}`, visibleFraction(polygon, rect));
        }
        const done = this.dwell.update(fractions, performance.now());
        if (done.length) {
            session.markSeen(done.map(key => {
                const [kind, level, idx] = key.split('|');
                return [kind, Number(level), Number(idx)];
            }));
        }
    };

    // ------------------------------------------------------------------ toggle, reset, other users

    _setVisibleChanged = (changed) => {
        if (!changed) return;
        try {
            localStorage.setItem(VISIBLE_STORAGE_KEY, this.toggle.persistent ? '1' : '0');
        } catch (e) { /* ignore */ }
        this._emit();
        this.scheduleDraw();
    };

    keyDown = (repeat) => this._setVisibleChanged(this.toggle.keyDown(performance.now(), repeat));

    keyUp = () => {
        const wasPersistent = this.toggle.persistent;
        const changed = this.toggle.keyUp(performance.now());
        this._setVisibleChanged(changed || wasPersistent !== this.toggle.persistent);
    };

    cancelKey = () => this._setVisibleChanged(this.toggle.cancel());

    toggleVisible = () => this._setVisibleChanged(this.toggle.setPersistent(!this.toggle.persistent));

    reset = async () => {
        const reset = await this.session.reset();
        this.dwell.reset();
        if (!this.viewingUser) this._displayBits = null;
        this._emit();
        return reset;
    };

    loadUsers = async () => {
        this.users = await this.session.listUsers();
        this._emit();
        return this.users;
    };

    /** Superusers: show another user's file on the minimap (own recording continues); null shows your own. */
    viewUser = async (user) => {
        if (!user || user.seen_area_id === this.session.seenAreaId) {
            this.viewingUser = null;
            this._displayBits = null;
        } else {
            const bits = await this.session.loadUserBits(user.seen_area_id);
            this.viewingUser = user;
            this._displayBits = bits || new Uint8Array(this.session.layout.byteLength);
        }
        this._pathsVersion = null;
        this._emit();
        this.scheduleDraw();
    };

    // ------------------------------------------------------------------ navigator canvas

    _attachViewerHandlers = () => {
        const viewer = this.viewer;
        if (!viewer) return;
        viewer.addHandler('rotate', this.scheduleDraw);
        viewer.addHandler('resize', this.scheduleDraw);
        viewer.addHandler('animation-finish', this.scheduleDraw);
        viewer.navigator?.addHandler('resize', this.scheduleDraw);
        viewer.navigator?.addHandler('animation', this.scheduleDraw);
    };

    _detachViewerHandlers = () => {
        const viewer = this.viewer;
        if (!viewer) return;
        viewer.removeHandler('rotate', this.scheduleDraw);
        viewer.removeHandler('resize', this.scheduleDraw);
        viewer.removeHandler('animation-finish', this.scheduleDraw);
        viewer.navigator?.removeHandler('resize', this.scheduleDraw);
        viewer.navigator?.removeHandler('animation', this.scheduleDraw);
    };

    scheduleDraw = () => {
        if (this._drawRaf != null) return;
        this._drawRaf = requestAnimationFrame(() => {
            this._drawRaf = null;
            this.draw();
        });
    };

    _ensureCanvas = () => {
        const navigator = this.viewer?.navigator;
        const container = navigator?.container;
        if (!container) return null;
        if (!this._canvas || this._canvas.parentNode !== container) {
            this._canvas?.parentNode?.removeChild(this._canvas);
            const canvas = document.createElement('canvas');
            canvas.className = 'osd-seen-area-canvas';
            canvas.setAttribute('aria-hidden', 'true');
            // Under the display-region box so the current-view rectangle stays on top.
            const region = navigator.displayRegionContainer;
            if (region?.parentNode === container) container.insertBefore(canvas, region);
            else container.appendChild(canvas);
            this._canvas = canvas;
        }
        return this._canvas;
    };

    /** One Path2D per level in image pixels, rebuilt only when the shown bits change. */
    _levelPaths = (bits) => {
        const session = this.session;
        const hasTiled = hasTiledLevels(session.grid);
        if (hasTiled) this._getFieldRects();
        const version = `${session.version}|${this.viewingUser?.seen_area_id || ''}|${this._fieldRectsVersion}`;
        if (this._paths && this._pathsVersion === version) return this._paths;
        const slide = this.slide;
        const tileSize = getStitchedTileSize(slide);
        const {width, height} = getImageDimensions(slide);
        const fieldRects = hasTiled ? this._fieldRectsById : null;
        const paths = [];
        for (const entry of session.layout.levels) {
            const path = new Path2D();
            let empty = true;
            if (entry.kind === STITCHED) {
                const scale = Math.pow(2, session.grid.z_max - entry.level);
                const tw = tileSize.width * scale;
                const th = tileSize.height * scale;
                forEachSetBit(bits, entry, idx => {
                    const x = (idx % entry.cols) * tw;
                    const y = Math.floor(idx / entry.cols) * th;
                    path.rect(x, y, Math.min(tw, width - x), Math.min(th, height - y));
                    empty = false;
                });
            } else if (fieldRects) {
                forEachSetBit(bits, entry, idx => {
                    const rect = fieldRects.get(idx);
                    if (!rect) return;
                    path.rect(rect[0], rect[1], rect[2] - rect[0], rect[3] - rect[1]);
                    empty = false;
                });
            }
            if (!empty) paths.push({key: levelKey(entry.kind, entry.level), path});
        }
        this._paths = paths;
        this._pathsVersion = version;
        return paths;
    };

    /** H2: levels stacked coarse → fine in one colour at a low alpha; follows navigator rotation and size. */
    draw = () => {
        const session = this.session;
        const canvas = this._ensureCanvas();
        if (!canvas) return;
        const bits = this._displayBits || session?.seen;
        if (!this.toggle.visible || session?.status !== SESSION_STATUS.READY || !bits) {
            canvas.style.display = 'none';
            return;
        }
        const navigator = this.viewer.navigator;
        const item = this.viewer.world?.getItemCount() > 0 ? this.viewer.world.getItemAt(0) : null;
        const cw = navigator.container.clientWidth;
        const ch = navigator.container.clientHeight;
        if (!item || !navigator.viewport || cw <= 0 || ch <= 0) return;

        const dpr = window.devicePixelRatio || 1;
        const bw = Math.max(1, Math.floor(cw * dpr));
        const bh = Math.max(1, Math.floor(ch * dpr));
        if (canvas.width !== bw || canvas.height !== bh) {
            canvas.width = bw;
            canvas.height = bh;
            canvas.style.width = `${cw}px`;
            canvas.style.height = `${ch}px`;
        }
        canvas.style.display = 'block';
        const ctx = canvas.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, bw, bh);

        const {width, height} = getImageDimensions(this.slide);
        const toNav = (ix, iy) => navigator.viewport.pixelFromPoint(item.imageToViewportCoordinates(ix, iy, true), true);
        const p0 = toNav(0, 0);
        const p1 = toNav(width, 0);
        const p2 = toNav(0, height);
        ctx.setTransform(
            dpr * (p1.x - p0.x) / width, dpr * (p1.y - p0.y) / width,
            dpr * (p2.x - p0.x) / height, dpr * (p2.y - p0.y) / height,
            dpr * p0.x, dpr * p0.y);
        ctx.fillStyle = `rgba(${HEAT_RGB}, ${LEVEL_ALPHA})`;
        for (const {path} of this._levelPaths(bits)) ctx.fill(path);
    };
}
